/**
 * DMP Status Bar
 *
 * Shows DMP version, completion progress, icon legend, and export button.
 */

import { Download, Loader2, CheckCircle2, AlertCircle, Edit3 } from 'lucide-react';
import { finalStatusLabel } from '../../utils/finalStatusLabel';
import { artifactStatusDisplay, overrideDetailLine } from '../../features/finalGate/finalGate';

interface DMPStatusBarProps {
  version: number;
  status: string;
  override?: { by?: string; at?: unknown; reason?: string; checks?: string[]; count?: number } | null;
  totalSections: number;
  completeSections: number;
  totalPlaceholders: number;
  onExport: () => void;
  exporting: boolean;
}

export default function DMPStatusBar({
  version,
  status,
  override,
  totalSections,
  completeSections,
  totalPlaceholders,
  onExport,
  exporting,
}: DMPStatusBarProps) {
  const percent = totalSections > 0 ? Math.round((completeSections / totalSections) * 100) : 0;

  const statusLabel =
    status === 'draft'
      ? 'Draft'
      : status === 'in_review'
      ? 'In Review'
      : status === 'approved'
      ? (finalStatusLabel(status, override) ?? 'Final')
      : status;

  const statusColor =
    status === 'approved'
      ? (artifactStatusDisplay(status, override, '').overridden ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700')
      : status === 'in_review'
      ? 'bg-blue-100 text-blue-700'
      : 'bg-gray-100 text-gray-600';

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
        {/* Version & Status */}
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-700">v{version}.0</span>
          <span title={override ? overrideDetailLine(override) : undefined} className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor}`}>
            {statusLabel}
          </span>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 flex-1">
          <div className="flex-1 max-w-xs">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
          <span className="text-gray-500 text-xs whitespace-nowrap">
            {completeSections}/{totalSections} sections generated
          </span>
          {totalPlaceholders > 0 && (
            <span className="text-amber-600 text-xs whitespace-nowrap">
              ({totalPlaceholders} placeholders to fill)
            </span>
          )}
        </div>

        {/* Export button */}
        <button
          onClick={onExport}
          disabled={exporting}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export DOCX
        </button>
      </div>

      {/* Icon legend */}
      <div className="flex items-center gap-4 px-4 text-xs text-gray-400">
        <span className="flex items-center gap-1">
          <CheckCircle2 className="h-3.5 w-3.5 text-green-400" /> Complete
        </span>
        <span className="flex items-center gap-1">
          <AlertCircle className="h-3.5 w-3.5 text-amber-500" /> Has placeholders to fill
        </span>
        <span className="flex items-center gap-1">
          <Edit3 className="h-3.5 w-3.5 text-blue-500" /> Edited by you
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3.5 text-center text-amber-600 font-semibold">3</span> Placeholder count
        </span>
      </div>
    </div>
  );
}
